import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Divider,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import dayjs from 'dayjs';
import useAssignGate from 'hooks/workout/useAssignGate';
import { Link } from 'react-router-dom';
import type { Workout } from 'types';
import type { AssignmentTarget } from 'utils/workout.helpers';

import AssignToDatePill from './components/AssignToDatePill';
import PanelTemplate from './components/PanelTemplate';
import PanelWorkout from './components/PanelWorkout';
import useAssignTracking from './useAssignTracking';

export interface AssignWorkoutModalProps {
  opened: boolean;
  onClose: () => void;
  target: AssignmentTarget;
  /** Omit to let the coach pick the date — that is what renders the ASSIGN TO DATE pill. */
  date?: string;
  /** Called after a workout is assigned (e.g. to refresh parent workout UI). */
  onWorkoutAssigned?: (workout: Workout) => void;
  /** View to return to after creating a workout from here (defaults to agenda). */
  view?: 'week' | 'agenda';
  /** Set when the coach picked Change Workout — the assignment this one replaces. */
  replacesWorkoutId?: string;
  defaultVisibleToAthletes?: boolean;
}

const AssignWorkoutModal = ({
  opened,
  onClose,
  target,
  date,
  onWorkoutAssigned,
  view,
  replacesWorkoutId,
  defaultVisibleToAthletes = true,
}: AssignWorkoutModalProps): JSX.Element => {
  const theme = useMantineTheme();
  const { canMutate, todayInGym } = useAssignGate();
  const { trackDateChanged, trackCancelled } = useAssignTracking(target);
  const [activeTab, setActiveTab] = useState<string | null>('templates');
  const [pickedDate, setPickedDate] = useState(todayInGym);

  const isDateEditable = date === undefined;
  const effectiveDate = date ?? pickedDate;

  useEffect(() => {
    if (!opened) {
      setActiveTab('templates');
      setPickedDate(todayInGym);
    }
  }, [opened, todayInGym]);

  const handleDateChange = (next: string): void => {
    trackDateChanged(pickedDate, next);
    setPickedDate(next);
  };

  const handleDismiss = (): void => {
    trackCancelled();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleDismiss}
      title={
        <Stack spacing={2} px='sm' pt='sm'>
          <Title order={4}>Assign Workout to {target.name}</Title>
        </Stack>
      }
      size='xl'
      centered
      withinPortal
    >
      <Stack spacing='md' px='sm'>
        <Stack spacing='md'>
          {isDateEditable ? (
            <AssignToDatePill date={effectiveDate} onChange={handleDateChange} />
          ) : (
            <Text size='sm' color='dimmed'>
              {dayjs(effectiveDate).format('MMMM D, YYYY')}
            </Text>
          )}
          <Text size='sm' color='dimmed'>
            Choose from templates, previous workouts, or create a new workout from scratch.
          </Text>
        </Stack>

        <Divider color='gray.4' />

        <Group position='apart' align='flex-end' noWrap>
          <SegmentedControl
            size='sm'
            value={activeTab ?? 'templates'}
            onChange={value => setActiveTab(value as 'templates' | 'history')}
            data={[
              { label: 'Workout Templates', value: 'templates' },
              { label: 'Assigned Workouts', value: 'history' },
            ]}
          />
          <Button
            component={Link}
            to='/workouts/programming/create'
            state={{ date: effectiveDate, target, view, replacesWorkoutId }}
            leftIcon={<IconPlus size={theme.fontSizes.sm} />}
            variant='filled'
            color='kiloBlue'
            size='sm'
            disabled={!canMutate}
          >
            Create New
          </Button>
        </Group>

        <Box pt='md' sx={{ minHeight: 420 }}>
          {activeTab === 'history' ? (
            <PanelWorkout
              target={target}
              date={effectiveDate}
              onWorkoutAssigned={onWorkoutAssigned}
              onClose={onClose}
              view={view}
              replacesWorkoutId={replacesWorkoutId}
              defaultVisibleToAthletes={defaultVisibleToAthletes}
            />
          ) : (
            <PanelTemplate
              target={target}
              date={effectiveDate}
              onWorkoutAssigned={onWorkoutAssigned}
              onClose={onClose}
              view={view}
              replacesWorkoutId={replacesWorkoutId}
              defaultVisibleToAthletes={defaultVisibleToAthletes}
            />
          )}
        </Box>

        <Group position='right' pt='xs'>
          <Button variant='subtle' onClick={handleDismiss}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default AssignWorkoutModal;
